import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import { useToast } from "@/hooks/use-toast";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";

type Post = Tables<"posts">;
type SocialAccount = Tables<"social_accounts">;

export interface PostWithAccount extends Post {
  social_accounts: SocialAccount | null;
  post_media?: Array<{ media_url: string; media_type: string }>;
}

export const useScheduledPosts = () => {
  const [posts, setPosts] = useState<PostWithAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPosts, setSelectedPosts] = useState<Set<string>>(new Set());
  const { toast } = useToast();
  const { orgId } = useOrgPermissions();

  useEffect(() => {
    fetchScheduledPosts();

    const subscription = supabase
      .channel('scheduled-posts-changes')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'posts' },
        () => { fetchScheduledPosts(); }
      )
      .subscribe();

    return () => { subscription.unsubscribe(); };
  }, [orgId]);

  const fetchScheduledPosts = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      let query = supabase
        .from('posts')
        .select(`*, social_accounts(*), post_media(media_url, media_type)`)
        .in('status', ['scheduled', 'draft'])
        .order('scheduled_for', { ascending: true });

      if (orgId) {
        query = (query as any).eq('org_id', orgId);
      } else {
        query = query.eq('user_id', user.id);
      }

      const { data, error } = await query;
      if (error) throw error;
      setPosts(data as PostWithAccount[] || []);
    } catch (error) {
      console.error("Error fetching scheduled posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const updatePostDate = async (postId: string, newDate: Date) => {
    try {
      const { error } = await supabase
        .from("posts")
        .update({ scheduled_for: newDate.toISOString() })
        .eq("id", postId);

      if (error) throw error;

      await fetchScheduledPosts();

      toast({
        title: "Success",
        description: "Post rescheduled successfully",
      });
    } catch (error) {
      console.error("Error updating post:", error);
      toast({
        title: "Error",
        description: "Failed to reschedule post",
        variant: "destructive",
      });
    }
  };

  const bulkReschedule = async (postIds: string[], newDate: Date) => {
    try {
      const { error } = await supabase
        .from("posts")
        .update({ scheduled_for: newDate.toISOString() })
        .in("id", postIds);

      if (error) throw error;

      await fetchScheduledPosts();
      setSelectedPosts(new Set());

      toast({
        title: "Success",
        description: `${postIds.length} post(s) rescheduled`,
      });
    } catch (error) {
      console.error("Error bulk rescheduling:", error);
      toast({
        title: "Error",
        description: "Failed to reschedule posts",
        variant: "destructive",
      });
    }
  };

  const bulkDelete = async (postIds: string[]) => {
    try {
      const { error } = await supabase
        .from("posts")
        .delete()
        .in("id", postIds);

      if (error) throw error;

      await fetchScheduledPosts();
      setSelectedPosts(new Set());

      toast({
        title: "Success",
        description: `${postIds.length} post(s) deleted`,
      });
    } catch (error) {
      console.error("Error bulk deleting:", error);
      toast({
        title: "Error",
        description: "Failed to delete posts",
        variant: "destructive",
      });
    }
  };

  const duplicatePost = async (postId: string, newDate?: Date) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const originalPost = posts.find(p => p.id === postId);
      if (!originalPost) return;

      const { data: newPost, error } = await supabase
        .from("posts")
        .insert({
          user_id: user.id,
          content: originalPost.content,
          social_account_id: originalPost.social_account_id,
          scheduled_for: newDate?.toISOString() || originalPost.scheduled_for,
          status: "draft",
          is_ai_generated: originalPost.is_ai_generated,
        })
        .select()
        .single();

      if (error) throw error;

      // Copy media if exists
      if (originalPost.post_media && originalPost.post_media.length > 0 && newPost) {
        const mediaRecords = originalPost.post_media.map((m, idx) => ({
          post_id: newPost.id,
          media_url: m.media_url,
          media_type: m.media_type,
          order_index: idx,
        }));

        await supabase.from("post_media").insert(mediaRecords);
      }

      await fetchScheduledPosts();

      toast({
        title: "Success",
        description: "Post duplicated successfully",
      });
    } catch (error) {
      console.error("Error duplicating post:", error);
      toast({
        title: "Error",
        description: "Failed to duplicate post",
        variant: "destructive",
      });
    }
  };

  const togglePostSelection = (postId: string) => {
    const newSelected = new Set(selectedPosts);
    if (newSelected.has(postId)) {
      newSelected.delete(postId);
    } else {
      newSelected.add(postId);
    }
    setSelectedPosts(newSelected);
  };

  const selectAll = () => {
    setSelectedPosts(new Set(posts.map(p => p.id)));
  };

  const clearSelection = () => {
    setSelectedPosts(new Set());
  };

  return {
    posts,
    loading,
    selectedPosts,
    refetch: fetchScheduledPosts,
    updatePostDate,
    bulkReschedule,
    bulkDelete,
    duplicatePost,
    togglePostSelection,
    selectAll,
    clearSelection,
  };
};
